import React, { useEffect, useState } from 'react';
import { Input, Modal } from 'antd';
import ConditionalRender from '../ConditionalRender';
import { useAnchoredPosition } from '../../../hooks/useAnchoredPosition';
import styles from './customWarning.module.scss';

interface CustomWarningProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  message: string | React.ReactNode;
  title?: string;
  cancelText?: string;
  confirmText?: string;
  variant?: 'light' | 'dark';
  confirmKeyword?: string;
  keywordPlaceholder?: string;
  showIcon?: boolean;
  /** For a plain acknowledgement with nothing to go back from. */
  hideCancel?: boolean;
  /** Let a backdrop click cancel, for a dialog where dismissing is harmless. */
  maskClosable?: boolean;
  /** Let Esc cancel; turn off when cancel is itself an action rather than a dismissal. */
  keyboard?: boolean;
  /** Dims the backdrop; off by default since most callers nest inside an already-dimmed modal. */
  maskVisible?: boolean;
  size?: 'default' | 'large';
  isLoading?: boolean;
  /** Open against this element instead of centred. */
  anchorEl?: HTMLElement | null;
  /** Runs once the close animation has finished. */
  afterClose?: () => void;
}

const CustomWarning: React.FC<CustomWarningProps> = ({
  open,
  onCancel,
  onConfirm,
  message,
  title,
  cancelText = 'Back',
  confirmText = 'Close',
  variant = 'light',
  confirmKeyword,
  keywordPlaceholder = `Type "${confirmKeyword}" to confirm`,
  showIcon = true,
  hideCancel = false,
  maskClosable = false,
  keyboard = true,
  maskVisible = false,
  size = 'default',
  isLoading = false,
  anchorEl,
  afterClose,
}) => {
  const { measureRef, style: anchoredStyle } = useAnchoredPosition({ anchorEl, enabled: open });
  const [inputValue, setInputValue] = useState('');
  const isConfirmEnabled = !confirmKeyword || inputValue.trim() === confirmKeyword;

  // This state sits above the Modal, so destroyOnClose doesn't clear it and a
  // reopened dialog would start with the keyword already typed.
  useEffect(() => {
    if (!open) setInputValue('');
  }, [open]);

  const sizeClass = size === 'large' ? styles.sizeLarge : styles.sizeDefault;
  const keywordClass = confirmKeyword ? styles.hasKeyword : '';

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      afterClose={afterClose}
      centered={!anchoredStyle}
      style={anchoredStyle}
      mask
      maskClosable={maskClosable}
      keyboard={keyboard}
      closable={false}
      footer={null}
      rootClassName={`${styles.customWarningRoot} ${maskVisible ? '' : styles.maskHidden}`}
      className={`${styles.customWarningModal} ${sizeClass} ${keywordClass}`}
      destroyOnClose
    >
      <div ref={measureRef} className={`${styles.container} ${styles[variant]} ${sizeClass}`}>
        <ConditionalRender visible={title}>
          <p className={styles.title}>{title}</p>
        </ConditionalRender>
        <ConditionalRender visible={showIcon}>
          <img src="/assets/warning-sign-icon.svg" alt="warning" className={styles.icon} />
        </ConditionalRender>
        <p className={title ? styles.body : styles.message}>{message}</p>
        <ConditionalRender visible={confirmKeyword}>
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={keywordPlaceholder}
            className={styles.keywordInput}
          />
        </ConditionalRender>
        <div className={styles.actions}>
          <ConditionalRender visible={!hideCancel}>
            <button type="button" className={styles.btn} onClick={onCancel} disabled={isLoading}>
              {cancelText}
            </button>
          </ConditionalRender>
          <button type="button" className={styles.btn} onClick={onConfirm} disabled={!isConfirmEnabled || isLoading}>
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CustomWarning;
